import { Formik, Form } from "formik";
import * as Yup from "yup";
import { ModalCompAdvance } from "../../Ui/ModalComp";
import { TextInputComp } from "../..";
import InvestorPortalActionButton from "./InvestorPortalActionButton";

const schema = Yup.object({
  name: Yup.string().required("Name is required"),
  kind: Yup.string().oneOf(["category", "subcategory"]).required(),
  parentId: Yup.string().when("kind", {
    is: "subcategory",
    then: Yup.string().required("Select a parent category"),
    otherwise: Yup.string().nullable(),
  }),
  sortOrder: Yup.number().min(0),
});

export default function AdminCategoryModal({ show, category, parentOptions = [], onClose, onSubmit }) {
  const isEdit = Boolean(category?.id);
  const initialKind = category?.parentId ? "subcategory" : "category";

  const body = (
    <Formik
      initialValues={{
        name: category?.name || "",
        kind: initialKind,
        parentId: category?.parentId || "",
        sortOrder: category?.sortOrder ?? 1,
        isActive: category?.isActive !== false,
      }}
      validationSchema={schema}
      onSubmit={(values) => {
        onSubmit({
          ...values,
          parentId: values.kind === "subcategory" ? values.parentId || null : null,
          sortOrder: Number(values.sortOrder) || 1,
        });
        onClose();
      }}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, touched, isSubmitting }) => (
        <Form>
          <div className="mb-3">
            <label className="form-label small fw-semibold">Type</label>
            <div className="d-flex flex-column gap-2">
              <label className="border rounded-3 p-3 mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="form-check-input me-2"
                  name="kind"
                  checked={values.kind === "category"}
                  onChange={() => {
                    setFieldValue("kind", "category");
                    setFieldValue("parentId", "");
                  }}
                />
                <strong>Category</strong>
                <div className="small text-muted mt-1 ms-4">
                  Top-level group (e.g. Food, Electronics).
                </div>
              </label>
              <label className="border rounded-3 p-3 mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="form-check-input me-2"
                  name="kind"
                  checked={values.kind === "subcategory"}
                  onChange={() => setFieldValue("kind", "subcategory")}
                  disabled={parentOptions.length === 0}
                />
                <strong>Subcategory</strong>
                <div className="small text-muted mt-1 ms-4">
                  Belongs under a category (e.g. Fish under Food).
                  {parentOptions.length === 0 ? (
                    <span className="d-block text-warning mt-1">
                      Create a category first before adding a subcategory.
                    </span>
                  ) : null}
                </div>
              </label>
            </div>
          </div>

          <TextInputComp
            label={values.kind === "subcategory" ? "Subcategory name" : "Category name"}
            name="name"
          />

          {values.kind === "subcategory" ? (
            <div className="mb-3">
              <label className="form-label small fw-semibold">Category</label>
              <select
                className="form-select"
                value={values.parentId || ""}
                onChange={(e) => setFieldValue("parentId", e.target.value)}
              >
                <option value="">Select category</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {touched.parentId && errors.parentId ? (
                <small className="text-danger">{errors.parentId}</small>
              ) : null}
            </div>
          ) : null}

          <TextInputComp label="Sort order" name="sortOrder" type="number" />
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="catActive"
              checked={values.isActive}
              onChange={(e) => setFieldValue("isActive", e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="catActive">
              Active
            </label>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <InvestorPortalActionButton
              btnText="Cancel"
              type="button"
              variant="ghost"
              fullWidth={false}
              onClick={onClose}
            />
            <InvestorPortalActionButton
              btnText={
                isEdit
                  ? "Save changes"
                  : values.kind === "subcategory"
                    ? "Create subcategory"
                    : "Create category"
              }
              type="submit"
              variant="primary"
              fullWidth={false}
              disabled={isSubmitting || (values.kind === "subcategory" && parentOptions.length === 0)}
            />
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <ModalCompAdvance
      show={show}
      handleClose={onClose}
      modalTitle={
        isEdit
          ? category?.parentId
            ? "Edit subcategory"
            : "Edit category"
          : "Create category or subcategory"
      }
      close
      size="md"
      centered
      arrayComp={[{ component: body }]}
    />
  );
}
